import { Navigate, useLocation } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useUserProfile } from '@/hooks/useSupabaseAuth'
import { resolveRoute } from '@/lib/workspace'
import { Button } from '@/components/ui/button'
import { roleMeetsRequirement } from '@/lib/roles'
import type { UserRole } from '@/types/icrop'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: UserRole
}

function insufficientRoleFallback(): string {
  return resolveRoute().kind === 'admin' ? '/no-account' : '/dashboard'
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading, signOut } = useAuth()
  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
    error: profileErrorObj,
    refetch: refetchProfile,
  } = useUserProfile()
  const location = useLocation()

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // The profile query failed (timeout / network / RLS). Show a recoverable
  // error instead of spinning forever or bouncing a real super_admin to
  // /no-account — and surface the actual message so the cause is visible.
  if (requiredRole && profileError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-1">Couldn't load your profile</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {profileErrorObj instanceof Error ? profileErrorObj.message : 'Unexpected error'}
          </p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => refetchProfile()}>Retry</Button>
            <Button variant="outline" onClick={() => signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (requiredRole) {
    if (!profile) {
      return <Navigate to={insufficientRoleFallback()} replace />
    }

    if (!roleMeetsRequirement(profile.role as UserRole, requiredRole)) {
      return <Navigate to={insufficientRoleFallback()} replace />
    }
  }

  return <>{children}</>
}
